import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import {
  Download, Search, DollarSign, Clock, Users,
  MapPin, Moon, Printer, ChevronRight, AlertCircle,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { isoDate } from '../../utils/time';

function StatCard({ label, value, sub, color = 'blue' }) {
  const colors = {
    blue:   'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
    green:  'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-700',
    violet: 'from-violet-50 to-violet-100 border-violet-200 text-violet-700',
    amber:  'from-amber-50 to-amber-100 border-amber-200 text-amber-700',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4`}>
      <p className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-extrabold">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function PayslipReport() {
  const today = new Date();
  const [startDate, setStartDate] = useState(
    isoDate(new Date(today.getFullYear(), today.getMonth(), 1))
  );
  const [endDate, setEndDate] = useState(
    isoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0))
  );
  const [staffList,   setStaffList]   = useState([]);
  const [staffFilter, setStaffFilter] = useState('');
  const [search,      setSearch]      = useState('');
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const printRef = useRef();

  useEffect(() => {
    api.get('/staff').then(r => setStaffList(r.data?.staff || r.data || [])).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffFilter) params.set('staff_id', staffFilter);
    api.get(`/reports/payslip-summary?${params}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load payslip report'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = (data?.staff || []).filter(s =>
    !search || `${s.first_name} ${s.last_name}`.toLowerCase().includes(search.toLowerCase())
  );

  const totals = data?.totals || {};

  const exportCsv = () => {
    if (!filtered.length) return;
    const rows = [
      ['Name', 'Employment Type', 'Pay Group', 'SCHADS Level', 'Shifts', 'Hours', 'Gross Pay',
       'Mileage km', 'Mileage $', 'Expenses', 'Sleepovers', 'Sleepover $', 'Total Payable',
       'Bank BSB', 'Bank Account', 'Super Fund', 'Super Member'],
      ...filtered.map(s => [
        `${s.first_name} ${s.last_name}`,
        s.employment_type || '',
        s.pay_group_name || s.pay_group || '',
        s.schads_level || '',
        s.shift_count,
        s.total_hours,
        s.gross_pay,
        s.mileage_km,
        s.mileage_amount,
        s.total_expenses,
        s.sleepovers,
        s.sleepover_amount,
        s.total_payable,
        s.bank_bsb || '',
        s.bank_account_number || '',
        s.super_fund_name || '',
        s.super_member_number || '',
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `payslip-report-${startDate}-${endDate}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Payroll pay-run export (server-built, Xero/KeyPay-friendly). includePii adds bank/TFN/super-fund.
  const exportPayRun = async (includePii) => {
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (staffFilter) params.set('staff_id', staffFilter);
      if (includePii) params.set('includePii', 'true');
      const r = await api.get(`/reports/payroll-export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `payrun-${startDate}-to-${endDate}${includePii ? '-full' : ''}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export pay run'); }
  };

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Payslip Report</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: system-ui, sans-serif; font-size: 12px; color: #1e293b; }
        h1 { font-size: 20px; font-weight: 800; color: #4f46e5; margin-bottom: 4px; }
        p.sub { font-size: 12px; color: #64748b; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th { background: #f1f5f9; text-align: left; padding: 7px 8px; font-size: 10px;
             text-transform: uppercase; color: #64748b; border-bottom: 2px solid #e2e8f0; }
        td { padding: 7px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        tr:last-child td { border-bottom: none; }
        .tfoot td { background: #eef2ff; font-weight: 700; border-top: 2px solid #6366f1; }
        .right { text-align: right; }
        .badge { display:inline-block; padding: 1px 6px; border-radius: 20px; font-size: 10px;
                 background: #e0e7ff; color: #4f46e5; font-weight: 600; }
        @media print { body { padding: 20px; } }
      </style></head><body>${content}</body></html>`);
    win.document.close(); win.focus(); win.print(); win.close();
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Payslip Summary Report</h1>
          <p className="text-sm text-slate-400 mt-0.5">Payroll summary of approved timesheets by staff member</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportPayRun(false)} className="btn-primary gap-1.5" title="Pay-run CSV for your payroll software (Xero/KeyPay)">
            <Download size={14} /> Pay-run CSV
          </button>
          <button onClick={() => exportPayRun(true)} className="btn-ghost gap-1.5" title="Pay-run CSV including bank & TFN (for new-employee setup)">
            <Download size={14} /> + Bank/TFN
          </button>
          {data && (
            <>
              <button onClick={handlePrint} className="btn-ghost gap-1.5">
                <Printer size={14} /> Print
              </button>
              <button onClick={exportCsv} className="btn-ghost gap-1.5">
                <Download size={14} /> Export CSV
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">From</label>
          <input type="date" className="input-field" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">To</label>
          <input type="date" className="input-field" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Staff Member</label>
          <select className="input-field min-w-[180px]" value={staffFilter} onChange={e => setStaffFilter(e.target.value)}>
            <option value="">All Staff</option>
            {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </select>
        </div>
        <button onClick={load} disabled={loading} className="btn-primary self-end">
          {loading ? 'Loading…' : 'Run Report'}
        </button>
        <div className="relative ml-auto self-end">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input-field pl-8 min-w-[180px]"
            placeholder="Search staff…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Stat cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard color="blue"   label="Staff Members" value={filtered.length}                     sub={`of ${data.staff.length} total`} />
          <StatCard color="green"  label="Total Payable"  value={`$${totals.total_payable?.toFixed(2) || '0.00'}`} sub="Wages + allowances" />
          <StatCard color="violet" label="Total Hours"    value={`${totals.total_hours?.toFixed(1) || 0}h`}         sub={`${totals.shift_count || 0} shifts approved`} />
          <StatCard color="amber"  label="Gross Wages"    value={`$${totals.gross_pay?.toFixed(2) || '0.00'}`}     sub="Before allowances" />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="card p-20 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Table */}
      {!loading && data && (
        <div className="card overflow-hidden" ref={printRef}>
          {/* Print header */}
          <div className="hidden print:block px-6 py-4 border-b border-slate-200">
            <h1>Payslip Summary Report</h1>
            <p className="sub">Period: {format(new Date(startDate), 'd MMM yyyy')} – {format(new Date(endDate), 'd MMM yyyy')} · Generated {format(new Date(), 'dd/MM/yyyy h:mm a')}</p>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-slate-400">
              <AlertCircle size={32} className="text-slate-300" />
              <p className="font-medium text-slate-500">No approved timesheets found for this period</p>
              <p className="text-sm">Timesheets must be approved to appear on payslips.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Staff</th>
                    <th className="text-left px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Pay Group</th>
                    <th className="text-center px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Shifts</th>
                    <th className="text-right px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Hours</th>
                    <th className="text-right px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Gross Wages</th>
                    <th className="text-right px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Mileage</th>
                    <th className="text-right px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Expenses</th>
                    <th className="text-right px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Sleepovers</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Total Payable</th>
                    <th className="text-center px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Payslip</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => (
                    <tr key={s.id} className={clsx('border-b border-slate-100 hover:bg-slate-50/50 transition-colors', i % 2 === 0 ? 'bg-white' : 'bg-slate-50/20')}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {s.first_name?.[0]}{s.last_name?.[0]}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{s.first_name} {s.last_name}</p>
                            <p className="text-[10px] text-slate-400 capitalize">{s.employment_type?.replace('_',' ') || 'Casual'}</p>
                          </div>
                        </div>
                        {/* Banking & super info */}
                        {(s.bank_bsb || s.super_fund_name) && (
                          <div className="mt-1.5 text-[10px] text-slate-400 space-y-0.5">
                            {s.bank_bsb && <p><span className="font-medium">BSB:</span> {s.bank_bsb} {s.bank_account_number && `| Acct: ***${s.bank_account_number.slice(-3)}`}</p>}
                            {s.super_fund_name && <p><span className="font-medium">Super:</span> {s.super_fund_name} {s.super_member_number && `(${s.super_member_number})`}</p>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {s.pay_group_name || s.pay_group
                          ? <><p className="font-medium text-slate-700 text-xs">{s.pay_group_name || s.pay_group}</p>
                              {s.schads_level && <p className="text-[10px] text-slate-400">SCHADS {s.schads_level}</p>}
                              {s.base_hourly_rate && <p className="text-[10px] text-slate-400">${parseFloat(s.base_hourly_rate).toFixed(2)}/hr base</p>}</>
                          : <span className="text-slate-400 text-xs">—</span>
                        }
                      </td>
                      <td className="px-3 py-3 text-center text-slate-700 font-medium">{s.shift_count}</td>
                      <td className="px-3 py-3 text-right font-mono font-semibold text-slate-700">{parseFloat(s.total_hours).toFixed(2)}h</td>
                      <td className="px-3 py-3 text-right font-semibold text-slate-800">${parseFloat(s.gross_pay).toFixed(2)}</td>
                      <td className="px-3 py-3 text-right">
                        {parseFloat(s.mileage_km) > 0
                          ? <><p className="font-medium text-slate-700">${s.mileage_amount.toFixed(2)}</p>
                              <p className="text-[10px] text-slate-400">{parseFloat(s.mileage_km).toFixed(1)}km</p></>
                          : <span className="text-slate-300">—</span>
                        }
                      </td>
                      <td className="px-3 py-3 text-right">
                        {parseFloat(s.total_expenses) > 0
                          ? <span className="font-medium text-slate-700">${parseFloat(s.total_expenses).toFixed(2)}</span>
                          : <span className="text-slate-300">—</span>
                        }
                      </td>
                      <td className="px-3 py-3 text-right">
                        {parseInt(s.sleepovers) > 0
                          ? <><p className="font-medium text-slate-700">${s.sleepover_amount.toFixed(2)}</p>
                              <p className="text-[10px] text-slate-400">{s.sleepovers} night{s.sleepovers !== 1 ? 's' : ''}</p></>
                          : <span className="text-slate-300">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-base font-extrabold text-primary-700">${s.total_payable.toFixed(2)}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <a
                          href={`/payslips?staffId=${s.id}&weekStart=${startDate}&weekEnd=${endDate}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 hover:underline"
                          onClick={e => { e.preventDefault(); window.location.href = `/payslips?staffId=${s.id}`; }}
                        >
                          View <ChevronRight size={11} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-indigo-50 border-t-2 border-indigo-200">
                    <td colSpan={2} className="px-4 py-3 font-bold text-slate-700 text-sm">
                      Totals — {filtered.length} staff member{filtered.length !== 1 ? 's' : ''}
                    </td>
                    <td className="px-3 py-3 text-center font-bold text-slate-700">{filtered.reduce((a,s)=>a+parseInt(s.shift_count),0)}</td>
                    <td className="px-3 py-3 text-right font-bold font-mono text-indigo-700">{filtered.reduce((a,s)=>a+parseFloat(s.total_hours),0).toFixed(2)}h</td>
                    <td className="px-3 py-3 text-right font-bold text-indigo-700">${filtered.reduce((a,s)=>a+parseFloat(s.gross_pay),0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-right font-bold text-indigo-700">${filtered.reduce((a,s)=>a+s.mileage_amount,0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-right font-bold text-indigo-700">${filtered.reduce((a,s)=>a+parseFloat(s.total_expenses),0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-right font-bold text-indigo-700">${filtered.reduce((a,s)=>a+s.sleepover_amount,0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-extrabold text-primary-700 text-base">${filtered.reduce((a,s)=>a+s.total_payable,0).toFixed(2)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
